import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { PwaService } from '../../util/pwa.service';
import { TenantService } from '../../services/tenant.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'black-pwa-install-banner',
  templateUrl: './pwa-install-banner.component.html',
  styleUrls: ['./pwa-install-banner.component.css'],
  standalone: false,
})
export class PwaInstallBannerComponent implements OnInit, OnDestroy {
  private pwaService = inject(PwaService);
  private tenantService = inject(TenantService, { optional: true });

  showBanner = false;
  isClosing = false;
  private autoCloseTimer?: ReturnType<typeof setTimeout>;
  private promptSubscription?: Subscription;
  private readonly AUTO_CLOSE_DELAY = 10000;

  get tenantName(): string {
    return this.tenantService?.currentTenant?.name || 'Learnbird';
  }

  get appIcon(): string {
    const branding = this.tenantService?.currentTenant?.settings?.branding;
    return (
      branding?.logo || branding?.favicon || '/assets/icons/icon-96x96.png'
    );
  }

  ngOnInit(): void {
    const slug = this.tenantService?.getTenantSlug?.() || 'default';
    // Subscribe to PWA prompt availability
    this.promptSubscription = this.pwaService
      .getPromptAvailability()
      .subscribe((available) => {
        if (available && this.pwaService.shouldShowBanner(slug)) {
          // Small delay to ensure page is loaded
          setTimeout(() => {
            this.showBanner = true;
            this.startAutoCloseTimer();
          }, 500);
        }
      });
  }

  private startAutoCloseTimer(): void {
    this.autoCloseTimer = setTimeout(() => {
      this.closeBanner();
    }, this.AUTO_CLOSE_DELAY);
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = undefined;
    }
  }

  async onInstall(): Promise<void> {
    this.clearAutoCloseTimer();
    await this.pwaService.triggerInstallPrompt();
    
    // Close banner regardless of whether user accepted or dismissed the prompt
    this.closeBanner();
  }

  onClose(): void {
    this.clearAutoCloseTimer();
    // Mark as dismissed for 7 days
    const slug = this.tenantService?.getTenantSlug?.() || 'default';
    this.pwaService.markBannerDismissed(slug);
    this.closeBanner();
  }

  private closeBanner(): void {
    this.isClosing = true;
    
    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.showBanner = false;
      this.isClosing = false;
    }, 300); // Match animation duration
  }

  ngOnDestroy(): void {
    this.clearAutoCloseTimer();
    if (this.promptSubscription) {
      this.promptSubscription.unsubscribe();
    }
  }
}
